// import node module libraries
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';

// import custom components
// import bootstrap icons
import { ExclamationTriangleFill } from 'react-bootstrap-icons';
import { useEffect, useState } from 'react';
import AnneescolaireService from '../../services/AnneescolaireService';
import BudgetService from '../../services/BudgetService';

const OffcanvasAddBudgetForm = ({ onClick }) => {
	const [validated, setValidated] = useState(false);
	const [feedbackMessages, setFeedbackMessages] = useState({});
	const [showAlert, setShowAlert] = useState(false);
	const [alertMessage, setAlertMessage] = useState('');
	const [yearOptions, setYearOptions] = useState([]);

	useEffect(() => {
		const fetchYearOptions = async () => {
			try {
				const years = await AnneescolaireService.getYearOptions();
				setYearOptions(years);
			} catch (error) {
				console.error('Error fetching years options', error);
			}
		};

		fetchYearOptions();
	}, []);

	const YEAR_CHOICES = yearOptions;

	const CATEGORIE_CHOICES = [
		{ value: 'frais_scolarite', label: 'Frais de Scolarité' },
		{ value: 'subventions_dons', label: 'Subventions et Dons' },
		{ value: 'depenses_exploitation', label: "Dépenses d'Exploitation" },
		{ value: 'investissements', label: 'Investissements' },
		{ value: 'reserve_urgence', label: "Réserve d'Urgence" },
		{
			value: 'provisions_annee_suivante',
			label: "Provisions pour l'année suivante"
		},
		{ value: 'depenses_educatives', label: 'Dépenses Éducatives' },
		{ value: 'couts_administratifs', label: 'Coûts Administratifs' },
		{
			value: 'depenses_services_publics',
			label: 'Dépenses pour les Services Publics'
		},
		{ value: 'depenses_securite', label: 'Dépenses pour la Sécurité' }
	];

	const handleSubmit = async (event) => {
		event.preventDefault();
		const form = event.currentTarget;

		if (form.checkValidity() === false) {
			event.stopPropagation();
		} else {
			try {
				const budgetData = new FormData();

				// Add all your form data to the FormData
				budgetData.append('categorie', form.categorie.value);
				budgetData.append('prevision_depense', form.prevision_depense.value);
				budgetData.append('prevision_recette', form.prevision_recette.value);
				budgetData.append('description', form.description.value);
				budgetData.append('anneescolaire', form.anneescolaire.value);

				// Call the BudgetService to create the budget
				const response = await BudgetService.createBudget(budgetData);

				// Reset the form and state variables after successful creation
				form.reset();
				setValidated(false);
				setFeedbackMessages({});

				// Redirect the user to the list of budgets
				window.location.href = '/finances/budget';
			} catch (error) {
				console.error('Erreur lors de la création du budget', error);

				if (error.response && error.response.data) {
					const apiErrors = error.response.data;

					// If there are field-specific errors
					if (typeof apiErrors === 'object' && !Array.isArray(apiErrors)) {
						const newFeedbackMessages = {};

						for (const field in apiErrors) {
							newFeedbackMessages[field] = apiErrors[field][0];
						}

						setFeedbackMessages(newFeedbackMessages);
					}

					// If there's a general error detail message
					if (apiErrors.detail) {
						setAlertMessage(apiErrors.detail[0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					} else if (
						apiErrors['Il ya erreur'] &&
						apiErrors['Il ya erreur'][0]
					) {
						setAlertMessage(apiErrors['Il ya erreur'][0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					}
				} else {
					alert(
						"Une erreur s'est produite lors de la soumission du formulaire."
					);
				}
			}
		}

		setValidated(true);
	};

	return (
		<Form
			className="mb-3"
			noValidate
			validated={validated}
			onSubmit={handleSubmit}
		>
			{showAlert && (
				<Alert variant="danger">
					<ExclamationTriangleFill size={25} className="me-1" />
					{alertMessage}
				</Alert>
			)}
			<Row>
				{/* Categorie */}
				<Col xs={12} className="mb-3">
					<Form.Group className="mb-3">
						<Form.Label htmlFor="categorie">
							Catégorie <span className="text-danger">*</span>
						</Form.Label>
						<Form.Select
							name="categorie"
							required
							isInvalid={!!feedbackMessages['categorie']}
							onChange={(e) => {
								setFeedbackMessages((prevMessages) => ({
									...prevMessages,
									categorie: e.target.value
										? ''
										: 'La catégorie du budget est requise.'
								}));
							}}
						>
							<option value="">Selectionner une catégorie</option>
							{CATEGORIE_CHOICES.map((option, index) => (
								<option key={index} value={option.value}>
									{option.label}
								</option>
							))}
						</Form.Select>
						<Form.Control.Feedback type="invalid">
							{feedbackMessages['categorie']}
						</Form.Control.Feedback>
					</Form.Group>
				</Col>

				<Col xs={12} className="mb-3">
					{/* Dépenses et Recettes  */}
					<Form.Group>
						<Row>
							<Col md={6} sm={12} className="mb-3">
								<Form.Label htmlFor="prevision_depense">
									Prévision dépenses <span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									name="prevision_depense"
									type="number"
									min="25"
									placeholder="123456789 FCFA"
									id="prevision_depense"
									required
									isInvalid={!!feedbackMessages['prevision_depense']}
									onChange={(e) => {
										setFeedbackMessages((prevMessages) => ({
											...prevMessages,
											prevision_depense: e.target.value
												? ''
												: 'La valeur prévisionnelle des dépenses est requise.'
										}));
									}}
								/>
								<Form.Control.Feedback type="invalid">
									{feedbackMessages['prevision_depense']}
								</Form.Control.Feedback>
							</Col>
							<Col md={6} sm={12} className="mb-3">
								<Form.Label htmlFor="prevision_recette">
									Prévision recettes <span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									name="prevision_recette"
									type="number"
									min="25"
									placeholder="123456789 FCFA"
									id="prevision_recette"
									required
									isInvalid={!!feedbackMessages['prevision_recette']}
									onChange={(e) => {
										setFeedbackMessages((prevMessages) => ({
											...prevMessages,
											prevision_recette: e.target.value
												? ''
												: 'La valeur prévisionnelle des recettes est requise.'
										}));
									}}
								/>
								<Form.Control.Feedback type="invalid">
									{feedbackMessages['prevision_recette']}
								</Form.Control.Feedback>
							</Col>
						</Row>
					</Form.Group>
				</Col>

				{/* Description */}
				<Col xs={12} className="mb-3">
					<Form.Group className="mb-3">
						<Form.Label htmlFor="description">Description</Form.Label>
						<Form.Control
							name="description"
							as="textarea"
							placeholder="Brêve description de la prévision budgétaire"
							id="description"
							style={{ height: '100px' }}
						/>
						<Form.Control.Feedback type="invalid">
							{feedbackMessages['description']}
						</Form.Control.Feedback>
					</Form.Group>
				</Col>

				{/* Année scolaire */}
				<Col xs={12} className="mb-3">
					<Form.Label htmlFor="anneescolaire">Année scolaire</Form.Label>
					<Form.Select
						name="anneescolaire"
						required
						isInvalid={!!feedbackMessages['anneescolaire']}
						onChange={(e) => {
							const value = e.target.value;
							const feedbackMessage = value
								? ''
								: "L'année scolaire pour laquelle la prévision est faite est requise.";
						}}
					>
						<option value="">Selectionner l'année scolaire</option>
						{YEAR_CHOICES.map((choice, index) => (
							<option key={index} value={choice.value}>
								{choice.label}
							</option>
						))}
					</Form.Select>
					<Form.Control.Feedback type="invalid">
						{feedbackMessages['anneescolaire']}
					</Form.Control.Feedback>
				</Col>

				{/* Buttons */}
				<Col xs={12} className="mt-4">
					<Button variant="success" type="submit">
						Enregistrer
					</Button>
					<Button onClick={onClick} variant="outline-danger" className="ms-2">
						Fermer
					</Button>
				</Col>
			</Row>
		</Form>
	);
};

export default OffcanvasAddBudgetForm;
